import "maplibre-gl/dist/maplibre-gl.css";

import type { FeatureCollection, GeoJSON, LineString, Point } from "geojson";
import { ChevronRight, CircleHelp, MapPin, MousePointer2, Redo2, Route, Trash2, Undo2, Waypoints } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Map as MapLibreMap, type MapRef, Marker } from "react-map-gl/maplibre";
import {
  type GeoJSONStoreFeatures,
  TerraDraw,
  TerraDrawLineStringMode,
  TerraDrawPointMode,
  TerraDrawSelectMode,
  TerraDrawSessionUndoRedo,
} from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";

import { Button, Input, Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import {
  createLineStringBetweenPoints,
  createRouteEditorDocument,
  pointEntries,
  type RouteEditorDocument,
  type RouteEditorEntry,
  type RouteEditorFeature,
  type RouteEditorGeometry,
  serializeRouteEditorDocument,
} from "~/features/recordGeoItems/utils/routeEditorGeoJson";
import { mapStyle } from "~/lib/map/components/constants";
import { cn } from "~/lib/utils";

const MAP_HEIGHT = 560;
const POINT_COLOR = "#2563EB";
const POINT_SELECTED_COLOR = "#1D4ED8";
const LINE_STRING_COLOR = "#EA580C";
const LINE_STRING_SELECTED_COLOR = "#C2410C";
const INTERNAL_PROPERTIES = [
  "currentlyDrawing",
  "midPoint",
  "selectionPoint",
  "coordinatePoint",
  "closingPoint",
  "snappingPoint",
] as const;

interface RouteEditorMapProps {
  geometry: GeoJSON | null;
  onChange: (geometry: FeatureCollection) => void;
  onClose: () => void;
}

type EditorTool = "select" | "point" | "linestring" | "connect";

const isPersistableStoreFeature = (
  feature: GeoJSONStoreFeatures,
): feature is GeoJSONStoreFeatures<Point | LineString> => {
  if (feature.geometry.type !== "Point" && feature.geometry.type !== "LineString") return false;
  if (feature.properties.mode !== "point" && feature.properties.mode !== "linestring") return false;
  return INTERNAL_PROPERTIES.every((property) => !feature.properties[property]);
};

const editorProjection = (entry: RouteEditorEntry): GeoJSONStoreFeatures<Point | LineString> => ({
  type: "Feature",
  id: entry.key,
  properties: { mode: entry.feature.geometry.type === "Point" ? "point" : "linestring" },
  geometry: entry.feature.geometry,
});

const collectPositions = (document: RouteEditorDocument): [number, number][] =>
  document.entries.flatMap((entry) => {
    const { geometry } = entry.feature;
    if (geometry.type === "Point") return [[geometry.coordinates[0], geometry.coordinates[1]]];
    return geometry.coordinates.map((position) => [position[0], position[1]] as [number, number]);
  });

export function RouteEditorMap({ geometry, onChange, onClose }: RouteEditorMapProps) {
  const { t } = useTranslation();
  const mapRef = useRef<MapRef>(null);
  const drawRef = useRef<TerraDraw | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const initialDocumentRef = useRef<RouteEditorDocument | null>(null);
  if (!initialDocumentRef.current) {
    initialDocumentRef.current = createRouteEditorDocument(geometry, () => crypto.randomUUID());
  }
  const documentRef = useRef(initialDocumentRef.current);
  const [document, setDocument] = useState(initialDocumentRef.current);
  const [tool, setTool] = useState<EditorTool>("select");
  const [routePointKeys, setRoutePointKeys] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateHistoryState = useCallback(() => {
    setCanUndo(drawRef.current?.canUndo() ?? false);
    setCanRedo(drawRef.current?.canRedo() ?? false);
  }, []);

  const emitDocument = useCallback((nextDocument: RouteEditorDocument) => {
    const serialized = serializeRouteEditorDocument(nextDocument);
    documentRef.current = serialized.document;
    setDocument(serialized.document);
    onChangeRef.current(serialized.geoJson);
  }, []);

  const createCanonicalFeature = useCallback(
    (geometryToAdd: RouteEditorGeometry): RouteEditorFeature => {
      if (geometryToAdd.type === "Point") {
        const pointNumber = pointEntries(documentRef.current.entries).length + 1;
        return {
          type: "Feature",
          id: crypto.randomUUID(),
          properties: {
            name: t("feature.recordGeoItems.routeEditor.defaultPointName", { number: pointNumber }),
          },
          geometry: geometryToAdd,
        };
      }
      return {
        type: "Feature",
        id: crypto.randomUUID(),
        properties: {},
        geometry: geometryToAdd,
      };
    },
    [t],
  );

  const syncFromDraw = useCallback(
    (changedIds?: Array<string | number>) => {
      const draw = drawRef.current;
      if (!draw) return;

      const snapshots = draw.getSnapshot().filter(isPersistableStoreFeature);
      const snapshotByKey = new Map(snapshots.map((feature) => [String(feature.id), feature]));
      const changedKeys = changedIds ? new Set(changedIds.map(String)) : null;
      const knownKeys = new Set(documentRef.current.entries.map((entry) => entry.key));
      const entries = documentRef.current.entries
        .filter((entry) => snapshotByKey.has(entry.key))
        .map((entry) => ({
          ...entry,
          feature:
            !changedKeys || changedKeys.has(entry.key)
              ? { ...entry.feature, geometry: snapshotByKey.get(entry.key)?.geometry ?? entry.feature.geometry }
              : entry.feature,
        }));

      for (const snapshot of snapshots) {
        const key = String(snapshot.id);
        if (knownKeys.has(key)) continue;
        entries.push({
          key,
          originalIndex: null,
          feature: createCanonicalFeature(snapshot.geometry),
        });
      }

      emitDocument({ ...documentRef.current, entries });
      updateHistoryState();
    },
    [createCanonicalFeature, emitDocument, updateHistoryState],
  );

  const initializeDraw = useCallback(() => {
    if (drawRef.current || !mapRef.current) return;
    const map = mapRef.current.getMap();
    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map, prefixId: `route-editor-${crypto.randomUUID()}` }),
      idStrategy: {
        getId: () => crypto.randomUUID(),
        isValidId: (id): id is string => typeof id === "string",
      },
      modes: [
        new TerraDrawPointMode({
          styles: {
            pointColor: POINT_COLOR,
            pointOutlineColor: "#FFFFFF",
            pointOutlineWidth: 2,
            pointWidth: 7,
          },
        }),
        new TerraDrawLineStringMode({
          styles: {
            lineStringColor: LINE_STRING_COLOR,
            lineStringWidth: 4,
            coordinatePointColor: LINE_STRING_COLOR,
          },
          showCoordinatePoints: true,
          snapping: {
            toFeature: {
              filter: (feature) => feature.geometry.type === "Point",
              toCoordinate: true,
            },
          },
        }),
        new TerraDrawSelectMode({
          styles: {
            selectedPointColor: POINT_SELECTED_COLOR,
            selectedLineStringColor: LINE_STRING_SELECTED_COLOR,
          },
          flags: {
            point: { feature: { draggable: true } },
            linestring: {
              feature: {
                draggable: true,
                coordinates: { draggable: true, midpoints: true, deletable: true },
              },
            },
          },
        }),
      ],
      undoRedo: { sessionLevel: new TerraDrawSessionUndoRedo({ maxStackSize: 50 }) },
    });
    drawRef.current = draw;
    draw.start();

    const validations = draw.addFeatures(documentRef.current.entries.map(editorProjection));
    const invalid = validations.find((validation) => !validation.valid);
    if (invalid) {
      setError(invalid.reason ?? t("feature.recordGeoItems.routeEditor.errors.initializationFailed"));
      draw.stop();
      drawRef.current = null;
      return;
    }

    draw.setMode("select");
    draw.clearUndoRedoHistory();
    draw.on("finish", (id) => syncFromDraw([id]));
    draw.on("change", (ids, changeType) => {
      if (changeType === "update" || changeType === "delete") syncFromDraw(ids);
    });
    draw.on("select", setSelectedId);
    draw.on("deselect", () => setSelectedId(null));
    draw.on("history", updateHistoryState);
    updateHistoryState();

    const positions = collectPositions(documentRef.current);
    if (positions.length === 1) {
      map.jumpTo({ center: positions[0], zoom: 7 });
    } else if (positions.length > 1) {
      const longitudes = positions.map(([longitude]) => longitude);
      const latitudes = positions.map(([, latitude]) => latitude);
      map.fitBounds(
        [
          [Math.min(...longitudes), Math.min(...latitudes)],
          [Math.max(...longitudes), Math.max(...latitudes)],
        ],
        { padding: 64, maxZoom: 7, duration: 0 },
      );
    }
  }, [syncFromDraw, t, updateHistoryState]);

  useEffect(
    () => () => {
      drawRef.current?.stop();
      drawRef.current = null;
    },
    [],
  );

  const selectTool = (nextTool: EditorTool) => {
    drawRef.current?.setMode(nextTool === "connect" ? "select" : nextTool);
    setTool(nextTool);
    if (nextTool !== "select") setSelectedId(null);
    if (nextTool !== "connect") setRoutePointKeys([]);
  };

  const removeFeature = (id: string | number) => {
    drawRef.current?.removeFeatures([id]);
    setSelectedId(null);
    setRoutePointKeys((keys) => keys.filter((key) => key !== String(id)));
    syncFromDraw([id]);
  };

  const updatePointName = (key: string, name: string) => {
    const entries = documentRef.current.entries.map((entry) =>
      entry.key === key
        ? { ...entry, feature: { ...entry.feature, properties: { ...entry.feature.properties, name } } }
        : entry,
    );
    emitDocument({ ...documentRef.current, entries });
  };

  const points = pointEntries(document.entries);

  const addLineStringBetweenPoints = (pointKeys: [string, string]) => {
    const draw = drawRef.current;
    const lineString = createLineStringBetweenPoints(documentRef.current.entries, pointKeys);
    if (!draw || !lineString) return;

    const entry: RouteEditorEntry = {
      key: crypto.randomUUID(),
      originalIndex: null,
      feature: createCanonicalFeature(lineString),
    };
    const [validation] = draw.addFeatures([editorProjection(entry)]);
    if (!validation?.valid) {
      setError(validation?.reason ?? t("feature.recordGeoItems.routeEditor.errors.lineStringGenerationFailed"));
      return;
    }

    setError(null);
    syncFromDraw([entry.key]);
    selectTool("select");
    draw.selectFeature(entry.key);
    updateHistoryState();
  };

  const selectRoutePoint = (key: string) => {
    if (tool !== "connect") return;
    const [startPointKey] = routePointKeys;
    if (!startPointKey || startPointKey === key) {
      setRoutePointKeys(startPointKey === key ? [] : [key]);
      return;
    }
    addLineStringBetweenPoints([startPointKey, key]);
  };

  const startPointNumber = routePointKeys[0] ? points.findIndex((entry) => entry.key === routePointKeys[0]) + 1 : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={tool === "select" ? "default" : "outline"} onClick={() => selectTool("select")}>
          <MousePointer2 />
          {t("feature.recordGeoItems.routeEditor.select")}
        </Button>
        <div className="flex items-center gap-1 rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-1">
          <Button size="sm" variant={tool === "point" ? "default" : "ghost"} onClick={() => selectTool("point")}>
            <span className="flex size-5 items-center justify-center rounded-full border border-current text-12-bold">
              1
            </span>
            <MapPin />
            {t("feature.recordGeoItems.routeEditor.placePoint")}
          </Button>
          <ChevronRight className="size-4 text-fg-3" aria-hidden="true" />
          <Button
            size="sm"
            variant={tool === "connect" ? "default" : "ghost"}
            disabled={points.length < 2}
            onClick={() => selectTool("connect")}
          >
            <span className="flex size-5 items-center justify-center rounded-full border border-current text-12-bold">
              2
            </span>
            <Waypoints />
            {t("feature.recordGeoItems.routeEditor.addRoute")}
          </Button>
        </div>
        <div className="h-6 w-px bg-border-default" aria-hidden="true" />
        <Button
          size="sm"
          variant={tool === "linestring" ? "default" : "outline"}
          onClick={() => selectTool("linestring")}
        >
          <Route />
          {t("feature.recordGeoItems.routeEditor.lineString")}
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={t("feature.recordGeoItems.routeEditor.help.label")}
            >
              <CircleHelp />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="space-y-1">
            <p>{t("feature.recordGeoItems.routeEditor.help.point")}</p>
            <p>{t("feature.recordGeoItems.routeEditor.help.connectPoints")}</p>
            <p>{t("feature.recordGeoItems.routeEditor.help.lineString")}</p>
            <p>{t("feature.recordGeoItems.routeEditor.help.select")}</p>
            <p>{t("feature.recordGeoItems.routeEditor.help.history")}</p>
          </TooltipContent>
        </Tooltip>
        <Button
          size="icon"
          variant="outline"
          aria-label={t("feature.recordGeoItems.routeEditor.undo")}
          disabled={!canUndo}
          onClick={() => {
            drawRef.current?.undo();
            syncFromDraw();
          }}
        >
          <Undo2 />
        </Button>
        <Button
          size="icon"
          variant="outline"
          aria-label={t("feature.recordGeoItems.routeEditor.redo")}
          disabled={!canRedo}
          onClick={() => {
            drawRef.current?.redo();
            syncFromDraw();
          }}
        >
          <Redo2 />
        </Button>
        <Button
          size="sm"
          variant="danger-outline"
          disabled={selectedId === null}
          onClick={() => selectedId !== null && removeFeature(selectedId)}
        >
          <Trash2 />
          {t("ui.delete")}
        </Button>
        <Button size="sm" variant="outline" className="ml-auto" onClick={onClose}>
          {t("feature.recordGeoItems.routeEditor.finishEditing")}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-12-medium text-fg-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-3 rounded-full border-2 border-white" style={{ backgroundColor: POINT_COLOR }} />
          {t("feature.recordGeoItems.routeEditor.point")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1 w-5 rounded" style={{ backgroundColor: LINE_STRING_COLOR }} />
          {t("feature.recordGeoItems.routeEditor.lineString")}
        </span>
        {tool === "connect" ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="rounded-[var(--radius-pill)] border border-primary-500 bg-primary-50 px-2 py-0.5 text-primary-700">
              {t("feature.recordGeoItems.routeEditor.startPoint")} {startPointNumber ?? "?"}
            </span>
            <ChevronRight className="size-3.5 text-fg-3" aria-hidden="true" />
            <span className="rounded-[var(--radius-pill)] border border-border-default bg-white px-2 py-0.5 text-fg-3">
              {t("feature.recordGeoItems.routeEditor.endPoint")} ?
            </span>
          </span>
        ) : (
          <span>{t(`feature.recordGeoItems.routeEditor.toolDescription.${tool}`)}</span>
        )}
      </div>

      {error && <p className="text-12-medium text-danger">{error}</p>}

      <div
        className="overflow-hidden rounded-[var(--radius-md)] border border-border-default"
        style={{ height: MAP_HEIGHT }}
      >
        <MapLibreMap
          ref={mapRef}
          initialViewState={{ longitude: 0, latitude: 20, zoom: 1.5 }}
          mapStyle={mapStyle}
          dragRotate={false}
          doubleClickZoom={false}
          onLoad={initializeDraw}
        >
          {points.map((entry, index) => {
            const [longitude, latitude] = entry.feature.geometry.coordinates;
            const isRouteStartPoint = routePointKeys[0] === entry.key;
            return (
              <Marker
                key={entry.key}
                ref={(marker) => {
                  marker
                    ?.getElement()
                    .setAttribute(
                      "aria-label",
                      t("feature.recordGeoItems.routeEditor.selectPoint", { number: index + 1 }),
                    );
                }}
                longitude={longitude}
                latitude={latitude}
                anchor="center"
                onClick={(event) => {
                  event.originalEvent.stopPropagation();
                  selectRoutePoint(entry.key);
                }}
                style={{
                  cursor: tool === "connect" ? "pointer" : "default",
                  pointerEvents: tool === "connect" ? "auto" : "none",
                }}
              >
                <span
                  className={cn(
                    "pointer-events-none flex size-5 items-center justify-center rounded-full text-12-bold leading-none text-white",
                    isRouteStartPoint && "ring-2 ring-primary-500 ring-offset-2",
                  )}
                >
                  {index + 1}
                </span>
              </Marker>
            );
          })}
        </MapLibreMap>
      </div>

      {points.length > 0 && (
        <div className="space-y-2 rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-3">
          <p className="text-12-bold text-fg-2">{t("feature.recordGeoItems.routeEditor.pointList")}</p>
          {points.map((entry, index) => (
            <div key={entry.key} className="flex items-center gap-2">
              <span className="w-6 text-right text-12-bold tabular-nums text-fg-3">{index + 1}</span>
              <Input
                value={typeof entry.feature.properties?.name === "string" ? entry.feature.properties.name : ""}
                aria-label={t("feature.recordGeoItems.routeEditor.pointName", { number: index + 1 })}
                onChange={(event) => updatePointName(entry.key, event.target.value)}
              />
              <Button
                size="icon"
                variant="danger-outline"
                aria-label={t("ui.delete")}
                onClick={() => removeFeature(entry.key)}
              >
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
